import React from "react";
import { Link } from "react-router-dom";
import { FiArrowRight } from "react-icons/fi";
import SectionHeader from "../ui/SectionHeader";
import FadeIn from "../ui/FadeIn";
import HeroVisual from "../ui/HeroVisual";
import {
  hero,
  stats,
  aboutSummary,
  whyUs,
  process,
  services,
  allTechTags,
  benefits,
  clients,
  cta,
} from "../../content/companyProfile";
import globalbees from "../../images/globalbees.svg";
import instinct from "../../images/instinct.svg";
import "./Homepage.css";

const serviceHighlights = [
  "End-to-end web & mobile development",
  "Cloud hosting and scalable infrastructure",
  "QA, security audits, and compliance",
  "Dedicated team on contract or project basis",
];

const Homepage = () => {
  return (
    <main className="vp-home">
      {/* Hero — centered like Paved */}
      <section className="vp-hero">
        <div className="vp-container vp-hero__center">
          <FadeIn>
            <h1 className="vp-hero__title">
              Build software that{" "}
              <span className="vp-text-gradient">moves your business forward</span>
            </h1>
            <p className="vp-hero__subtitle">{hero.subheadline}</p>
            <div className="vp-hero__actions">
              <Link to={hero.primaryCta.path} className="vp-btn vp-btn--primary vp-btn--lg">
                Get Started
              </Link>
              {/* <Link to={hero.secondaryCta.path} className="vp-btn vp-btn--outline vp-btn--lg">
                View services
              </Link> */}
            </div>
          </FadeIn>
        </div>

        {/* <div className="vp-logo-bar">
          <div className="vp-container vp-logo-bar__inner">
            <img src={globalbees} alt="GlobalBees" />
            <img src={instinct} alt="Instinct" />
          </div>
        </div> */}

        <div className="vp-container vp-stats-inline">
          {stats.map((item) => (
            <div key={item.label} className="vp-stats-inline__item">
              <strong>{item.value}</strong>
              <span>{item.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Marketplace-style services split */}
      <section className="vp-section vp-section--subtle" id="services">
        <div className="vp-container vp-feature-split">
          <FadeIn className="vp-feature-split__cards">
            <div className="vp-mini-cards">
              {services.slice(0, 4).map(({ icon: Icon, title }) => (
                <article key={title} className="vp-mini-card">
                  <div className="vp-mini-card__icon">
                    <Icon aria-hidden />
                  </div>
                  <h3>{title}</h3>
                </article>
              ))}
            </div>
          </FadeIn>
          <FadeIn className="vp-feature-split__content" delay={0.1}>
            <span className="vp-eyebrow">Our services</span>
            <h2 className="vp-section-title">The full stack for modern products</h2>
            <p className="vp-section-desc">
              From discovery to deployment—we help startups and enterprises ship reliable
              software with the right technology at every layer.
            </p>
            <ul className="vp-check-list">
              {serviceHighlights.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <Link to="/services" className="vp-btn vp-btn--primary">
              Browse all services
              <FiArrowRight aria-hidden />
            </Link>
          </FadeIn>
        </div>
      </section>

      {/* About */}
      <section className="vp-section" id="about">
        <div className="vp-container vp-about-split">
          <FadeIn className="vp-about-split__text">
            <span className="vp-eyebrow">About us</span>
            <h2 className="vp-section-title">{aboutSummary.title}</h2>
            <p className="vp-about__lead">{aboutSummary.lead}</p>
            <p className="vp-about__body">{aboutSummary.body}</p>
          </FadeIn>
          <FadeIn className="vp-about-split__cards" delay={0.1}>
            <div className="vp-why-stack">
              {whyUs.map((item) => (
                <article key={item.title} className="vp-why-row">
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </article>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Process */}
      <section className="vp-section vp-section--subtle" id="process">
        <div className="vp-container">
          <FadeIn>
            <SectionHeader
              eyebrow="How we work"
              title="From idea to launch, step by step"
              description="A clear process so you always know where your project stands."
              align="center"
            />
          </FadeIn>
          <div className="vp-process-row">
            {process.map((step, i) => (
              <FadeIn key={step.title} delay={i * 0.06} y={12}>
                <article className="vp-process-pill">
                  <span className="vp-process-pill__num">{step.step}</span>
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </article>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Remaining services grid */}
      <section className="vp-section">
        <div className="vp-container">
          <FadeIn>
            <SectionHeader
              title="More ways we collaborate"
              description="Marketing, 3D, analytics, and beyond."
              align="center"
            />
          </FadeIn>
          <div className="vp-services-row">
            {services.slice(4).map(({ icon: Icon, title, description }, i) => (
              <FadeIn key={title} delay={i * 0.05}>
                <article className="vp-card vp-service-tile">
                  <div className="vp-service-card__icon">
                    <Icon aria-hidden />
                  </div>
                  <h3>{title}</h3>
                  <p>{description}</p>
                </article>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Tech */}
      <section className="vp-section vp-section--subtle">
        <div className="vp-container vp-tech-section">
          <FadeIn>
            <SectionHeader
              eyebrow="Technologies"
              title="Built with tools you can trust"
              align="center"
            />
          </FadeIn>
          <FadeIn delay={0.08}>
            <div className="vp-tech-cloud">
              {allTechTags.map((tag) => (
                <span key={tag} className="vp-tag">
                  {tag}
                </span>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Perks */}
      <section className="vp-section">
        <div className="vp-container">
          <FadeIn>
            <SectionHeader
              eyebrow="For startups"
              title={benefits.title}
              description={benefits.intro}
              align="center"
            />
          </FadeIn>
          <div className="vp-perks-row">
            {benefits.items.map((item, i) => (
              <FadeIn key={item.title} delay={i * 0.04}>
                <article className="vp-perk-tile">
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </article>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="vp-cta-block">
        <div className="vp-container vp-cta-block__inner">
          <FadeIn>
            <h2>{cta.title}</h2>
            <p>{cta.description}</p>
            <Link to={cta.button.path} className="vp-btn vp-btn--primary vp-btn--lg" style={{'marginTop': '2rem','marginBottom': '-2rem'}}>
              {cta.button.label}
              <FiArrowRight aria-hidden />
            </Link>
          </FadeIn>
        </div>
      </section>
    </main>
  );
};

export default Homepage;
